import { useEffect, useState } from 'react'
import { listTransactions, getTransaction, type Transaction, type TransactionDetail, type TxStatus } from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useAuth } from '../layout/AuthContext'
import { brandName, BRANDS, formatMoney, invoiceTabName } from '../lookups'
import { fill } from '../../shared/texts'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import Drawer from '../components/Drawer'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { PAGES_TRANSACTIONS_LIST_TEXTS as T } from '../../shared/texts'

// The one formatter (constants.ts): the record's currency, else the backoffice currency.
const money = (n: number, currency?: string) => formatMoney(n, currency)
function dateTime(s?: string) {
    if (!s) return '—'
    return new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}

const STATUS_LABEL: Record<TxStatus, string> = T.statusLabels
// Reuse the shared badge colours: paid reads like a sent invoice, reversals like a cancelled one.
const STATUS_CLASS: Record<TxStatus, string> = {
    open: 'draft',
    paid: 'sent',
    failed: 'void',
    expired: 'void',
    refunded: 'void',
    partially_refunded: 'finalized',
}

const KIND_LABEL: Record<string, string> = T.kindLabels

const PROVIDERS = [{ key: 'mollie', name: T.providerMollie }, { key: 'stripe', name: T.providerStripe }, { key: 'paypal', name: T.providerPaypal }]

export default function TransactionsList() {
    const { openTab } = useTabs()
    const version = useDataVersion('transactions') // live refresh while a test purchase runs
    const { preferences, savePreferences } = useAuth()

    const [status, setStatus] = useState('')
    const [brand, setBrand] = useState('')
    const [provider, setProvider] = useState('')
    const [kind, setKind] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')

    const list = useServerList<Transaction>(listTransactions, version, {
        sort: 'occurredAt', dir: 'desc', pageSize: preferences.tablePageSize?.transactions ?? DEFAULT_TABLE_PAGE_SIZE,
        params: {
            status: status || undefined, brand: brand || undefined,
            provider: provider || undefined, kind: kind || undefined,
            from: from || undefined, to: to || undefined,
        },
    })
    const cols = useColumnVisibility('transactions')

    const [detailId, setDetailId] = useState<string | null>(null)
    const [detail, setDetail] = useState<TransactionDetail | null>(null)
    useEffect(() => {
        if (!detailId) { setDetail(null); return }
        let alive = true
        getTransaction(detailId).then((d) => { if (alive) setDetail(d) }).catch(() => { if (alive) setDetail(null) })
        return () => { alive = false }
    }, [detailId])

    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), transactions: n } })
    }

    const columns: Column<Transaction>[] = [
        {
            key: 'occurredAt', header: T.date, sortField: 'occurredAt', defaultWidth: 130,
            render: (t) => dateTime(t.occurredAt ?? t.receivedAt),
        },
        { key: 'brand', header: T.brand, sortField: 'brand', defaultWidth: 110, render: (t) => brandName(t.brand) },
        {
            key: 'provider', header: T.psp, sortField: 'provider', defaultWidth: 80,
            render: (t) => PROVIDERS.find((p) => p.key === t.provider)?.name ?? t.provider,
        },
        {
            key: 'providerPaymentId', header: T.transaction, defaultWidth: 230,
            render: (t) => <code>{t.providerPaymentId}</code>,
        },
        { key: 'kind', header: T.kind, sortField: 'kind', defaultWidth: 120, render: (t) => KIND_LABEL[t.kind] ?? t.kind },
        {
            key: 'amount', header: T.amount, sortField: 'amount', className: 'right', defaultWidth: 110,
            render: (t) => money(t.amount, t.currency),
        },
        {
            key: 'amountRefunded', header: T.refunded, className: 'right', defaultWidth: 110,
            render: (t) => (t.amountRefunded > 0 ? money(t.amountRefunded, t.currency) : '—'),
        },
        {
            key: 'status', header: T.status, sortField: 'status', className: 'center', defaultWidth: 130,
            render: (t) => <span className={`badge ${STATUS_CLASS[t.status] ?? ''}`}>{STATUS_LABEL[t.status] ?? t.status}</span>,
        },
        { key: 'customerEmail', header: T.customer, sortField: 'customerEmail', defaultWidth: 200, render: (t) => t.customerEmail ?? '—' },
        { key: 'externalId', header: T.reference, defaultWidth: 200, render: (t) => t.externalId ?? '—' },
    ]

    const filtered = list.q.trim() !== '' || !!status || !!brand || !!provider || !!kind || !!from || !!to

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
            />

            {list.error && <div className="notice err">{list.error}</div>}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={filtered}
                search={{ value: list.q, onChange: list.setQ }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
                filters={<>
                    <label>
                        {T.status}
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">{T.all}</option>
                            {Object.entries(STATUS_LABEL).map(([k, name]) => <option key={k} value={k}>{name}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.brand}
                        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                            <option value="">{T.all}</option>
                            {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.psp}
                        <select value={provider} onChange={(e) => setProvider(e.target.value)}>
                            <option value="">{T.all}</option>
                            {PROVIDERS.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                        </select>
                    </label>
                    <label>
                        {T.kind}
                        <select value={kind} onChange={(e) => setKind(e.target.value)}>
                            <option value="">{T.all}</option>
                            <option value="first">{T.kindLabels.first}</option>
                            <option value="recurring">{T.kindLabels.recurring}</option>
                            <option value="oneoff">{T.kindLabels.oneoff}</option>
                        </select>
                    </label>
                    <label>
                        {T.from}
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </label>
                    <label>
                        {T.to}
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                    </label>
                </>}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.empty}</p>
                ) : (
                    <>
                        <DataTable
                            key={cols.resetKey}
                            tableId="transactions"
                            columns={columns}
                            hidden={cols.hidden}
                            rows={list.rows}
                            rowKey={(t) => t._id}
                            sort={list.sort}
                            dir={list.dir}
                            onSort={list.toggleSort}
                            onRowActivate={(t) => setDetailId(t._id)}
                        />
                        {/* Sums over the FILTERED set — the thing the log could never answer. */}
                        {list.extra.sums && (
                            <p className="muted" style={{ marginTop: 8 }}>
                                {T.sum}{money(list.extra.sums.amount, list.rows[0]?.currency)}
                                {list.extra.sums.refunded > 0 && <>{T.sumRefunded}{money(list.extra.sums.refunded, list.rows[0]?.currency)}</>}
                            </p>
                        )}
                    </>
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <Drawer open={!!detailId} title={T.transaction} onClose={() => setDetailId(null)} width={620}>
                {!detail ? <p className="muted">{T.loading}</p> : (
                    <div className="tx-detail">
                        <dl className="kv">
                            <dt>{T.transaction}</dt><dd><code>{detail.providerPaymentId}</code></dd>
                            <dt>{T.status}</dt><dd><span className={`badge ${STATUS_CLASS[detail.status] ?? ''}`}>{STATUS_LABEL[detail.status] ?? detail.status}</span></dd>
                            <dt>{T.amount}</dt><dd>{money(detail.amount, detail.currency)}{detail.amountRefunded > 0 && <>{T.refundedInline}{money(detail.amountRefunded, detail.currency)})</>}</dd>
                            <dt>{T.kind}</dt><dd>{KIND_LABEL[detail.kind] ?? detail.kind}</dd>
                            <dt>{T.brandPsp}</dt><dd>{brandName(detail.brand)} · {detail.provider}</dd>
                            <dt>{T.paidAt}</dt><dd>{dateTime(detail.occurredAt)}</dd>
                            <dt>{T.received}</dt><dd>{dateTime(detail.receivedAt)}</dd>
                            <dt>{T.reference}</dt><dd>{detail.externalId ?? '—'}</dd>
                            <dt>{T.customer}</dt><dd>{detail.customerEmail ?? '—'}</dd>
                            {detail.providerCustomerId && <><dt>{T.pspCustomer}</dt><dd><code>{detail.providerCustomerId}</code></dd></>}
                            {detail.providerSubscriptionId && <><dt>{T.pspSubscription}</dt><dd><code>{detail.providerSubscriptionId}</code></dd></>}
                        </dl>

                        <h4>{T.history}</h4>
                        <table className="tx-history">
                            <tbody>
                                {detail.history.map((h, i) => (
                                    <tr key={i}>
                                        <td>{dateTime(h.at)}</td>
                                        <td>{h.type === 'refund' ? T.refund : T.status}</td>
                                        <td>
                                            {h.status ?? '—'}
                                            {h.reason ? ` (${h.reason})` : ''}
                                            {h.refundId ? <> · <code>{h.refundId}</code></> : null}
                                        </td>
                                        <td className="right">{h.amount != null ? money(h.amount, detail.currency) : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <h4>{T.linked}</h4>
                        <div className="tx-links">
                            {detail.invoiceId ? (
                                <button className="link" onClick={() => openTab({
                                    id: `invoice-${detail.invoiceId!._id}`,
                                    name: invoiceTabName(detail.invoiceId!),
                                    component: 'invoice-form',
                                    config: { id: detail.invoiceId!._id },
                                })}>{fill(T.invoiceLink, { invoiceNumber: detail.invoiceId.invoiceNumber ?? '' })}</button>
                            ) : <span className="muted">{T.noInvoice}</span>}
                            {detail.subscriptionId && (
                                <button className="link" onClick={() => openTab({
                                    id: `subscription-${detail.subscriptionId!._id}`,
                                    name: fill(T.subscriptionLink, { externalId: detail.subscriptionId!.externalId ?? '' }).trim(),
                                    component: 'subscription-form',
                                    config: { id: detail.subscriptionId!._id },
                                })}>{fill(T.subscriptionLink, { externalId: detail.subscriptionId.externalId ?? '' })}</button>
                            )}
                            {detail.customerId && (
                                <button className="link" onClick={() => openTab({
                                    id: `customer-${detail.customerId!._id}`,
                                    name: detail.customerId!.company || detail.customerId!.displayName || T.customer,
                                    component: 'customer-form',
                                    config: { id: detail.customerId!._id },
                                })}>{detail.customerId.company || detail.customerId.displayName || T.customer}</button>
                            )}
                        </div>

                        {/* The log search you would otherwise type by hand. */}
                        <button className="link" style={{ marginTop: 12 }} onClick={() => openTab({
                            id: 'logs', name: T.logsTab, component: 'logs', config: { q: detail.providerPaymentId },
                        })}>{T.logEntries}</button>
                    </div>
                )}
            </Drawer>
        </div>
    )
}
